import React, { createContext, useMemo, useEffect } from 'react';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { Token } from '@marageti/z4-sdk/lib/auth';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
} from '@marageti/z4-lib';
import { ProviderProps } from '../../types';
import { ValueProps } from './types';
import {
  setAuthenticatedTraveler,
  setIsAuthenticated,
  resetAuthenticatedTraveler,
} from './actions';
import useAuthReducer from './use-auth-reducer';
import { authorizedRoles, getMatchingUser } from '../../../utils/auth';
import debugPromise from '../../../utils/debug-promise';

const getCookieValue = (name: string): any | undefined => {
  // First try to find environment-specific trips cookie
  const env = process.env.REACT_APP_STAGE || 'prod';
  const tripsCookieMatch = document.cookie.match(new RegExp(`(^| )${name}_trips_${env}=([^;]+)`));
  if (tripsCookieMatch) {
    try {
      return JSON.parse(decodeURIComponent(tripsCookieMatch[2]));
    } catch (error) {
      return decodeURIComponent(tripsCookieMatch[2]);
    }
  }

  // Fall back to default cookie
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch (error) {
      // If the cookie value is not a valid JSON string, return it as is
      return decodeURIComponent(match[2]);
    }
  }
  return undefined;
};

const shouldCallGetTraveler = (personType: string | undefined, token: Token): boolean => {
  if (!personType) return true;
  if (personType === 'traveler') return true;
  if (personType === 'teamMember' && token.superEmail) return true;
  return false;
};

const LocalStorageClient = new LocalStorageService();

export const AuthStore = createContext({} as ValueProps);

export const AuthProvider = ({ children }: ProviderProps) => {
  const [authState, authDispatch] = useAuthReducer();
  const apiClient = useApiClient();

  const clearAuthenticatedTraveler = () => {
    LocalStorageClient.removeFromLocalStorage('accessToken');
    LocalStorageClient.removeFromLocalStorage('email');
    LocalStorageClient.removeFromLocalStorage('superEmail');
    authDispatch(setIsAuthenticated(false));
    authDispatch(resetAuthenticatedTraveler());
  };

  const logoutAndRemoveAuthenticatedTraveler = () => apiClient.authClient.logout()
    .catch(() => undefined)
    .finally(() => {
      clearAuthenticatedTraveler();
    });

  const getPersonTypeFromCookies = (): string | undefined => {
    const userSessionTripsCookie = getCookieValue(`user_session_trips_${process.env.REACT_APP_STAGE}`);
    if (userSessionTripsCookie?.personType) return userSessionTripsCookie.personType;

    const userSessionCookie = getCookieValue('user_session');
    return userSessionCookie?.personType;
  };

  const getAccessToken = (): string | undefined => {
    const localStorageAccessToken = LocalStorageClient.getFromLocalStorage('accessToken');
    if (localStorageAccessToken) return localStorageAccessToken;

    const userSessionTripsCookie = getCookieValue(`user_session_trips_${process.env.REACT_APP_STAGE}`);
    if (userSessionTripsCookie?.sessionId) return userSessionTripsCookie.sessionId;

    const userSessionCookie = getCookieValue('user_session');
    const isTravelerOrMasquerader = userSessionCookie?.personType === 'traveler' || userSessionCookie?.personType === 'teamMember';
    if (isTravelerOrMasquerader && userSessionCookie?.sessionId) {
      return userSessionCookie.sessionId;
    }
    return undefined;
  };

  const setAccessToken = (accessToken: string) => {
    // Set Bearer token. This is required for when accessing the GetToken method through a user_session cookie.
    LocalStorageClient.addToLocalStorage('accessToken', accessToken, 90 * 24 * 60 * 60 * 1000);
    apiClient.instance.setAuthToken(accessToken);
  };

  /**
   * Rehydrate the trips session from a bearer token.
   *
   * When the token came from localStorage (a trips login), always attempt traveler
   * auth — cookie personType can be stale from another app (partner/oryx) and must
   * not wipe a valid trips accessToken on refresh.
   *
   * Cookie-only sessions still use shouldCallGetTraveler so agent/teamMember
   * cookies do not trigger a traveler fetch.
   */
  const authorizeUserFromToken = async (
    accessToken: string,
    { fromLocalStorage }: { fromLocalStorage: boolean },
  ) => {
    try {
      const token = await apiClient.authClient.getToken(accessToken);
      const personType = getPersonTypeFromCookies();
      if (!fromLocalStorage && !shouldCallGetTraveler(personType, token)) {
        authDispatch(setIsAuthenticated(false));
        authDispatch(resetAuthenticatedTraveler());
        return;
      }
      if (token.superEmail) {
        LocalStorageClient.addToLocalStorage('superEmail', token.superEmail);
      }
      const traveler = await apiClient.peopleClient.getTraveler(token.email);
      const matchingUser = getMatchingUser(traveler.email, traveler.userInfo);
      if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles, true)) {
        throw new Error('Not Authorized');
      }
      LocalStorageClient.addToLocalStorage('email', traveler.email);
      authDispatch(setAuthenticatedTraveler(traveler as Traveler));
      authDispatch(setIsAuthenticated(true));
    } catch {
      clearAuthenticatedTraveler();
    }
  };

  useEffect(() => {
    const localStorageAccessToken = LocalStorageClient.getFromLocalStorage('accessToken');
    const accessToken = getAccessToken();
    if (accessToken) {
      setAccessToken(accessToken);
      debugPromise(
        'authProvider:mount.authorizeUserFromToken',
        authorizeUserFromToken(accessToken, { fromLocalStorage: !!localStorageAccessToken }),
      );
    } else {
      clearAuthenticatedTraveler();
    }
  }, []);

  const value: ValueProps = useMemo(
    () => ({
      ...authState,
      sessionId: getAccessToken(),
      personType: getPersonTypeFromCookies(),
      authDispatch,
      removeAuthenticatedTraveler: logoutAndRemoveAuthenticatedTraveler,
      clearAuthenticatedTraveler,
      setIsAuthenticated: (isAuthenticated: boolean) => authDispatch(setIsAuthenticated(isAuthenticated)),
      setAuthenticatedTraveler: (traveler: Traveler) => authDispatch(setAuthenticatedTraveler(traveler)),
    }),
    [authState, authDispatch],
  );

  return <AuthStore.Provider value={value}>{children}</AuthStore.Provider>;
};
